import React from 'react';
import EditorialArticle from '../../components/redesign/EditorialArticle.jsx';
import SEO from '../../components/SEO.jsx';
import EmailCapture from '../../components/EmailCapture.jsx';
import { trackOutboundClick } from '../../utils/analytics.js';

const GH = 'https://github.com/Aakeeo/tunnelr';

const FEATURES = [
  { title: 'Unlimited tunnels, persistent URLs', desc: 'No session timeouts, no rotating URLs. Tunnels stay alive as long as you need — no 2-hour expiry.' },
  { title: 'Automatic SSL via Caddy', desc: "Every tunnel gets HTTPS automatically. Caddy handles Let's Encrypt — HTTP-01 for path mode, DNS-01 via Cloudflare for subdomain mode." },
  { title: 'Two routing modes', desc: 'Path mode (yourdomain.com/t/tunnel-id) with one DNS record, or subdomain mode (tunnel-id.yourdomain.com) for cleaner URLs with a wildcard.' },
  { title: 'Self-hosted on your infra', desc: 'Deploy on any VPS with Docker. Your webhook payloads, API traffic and dev data never touch a third-party server. Zero telemetry.' },
  { title: 'Single Go binary', desc: 'The client compiles to a single binary for Linux, macOS and Windows. No runtime dependencies. Cross-compile with one command.' },
  { title: 'MIT-licensed', desc: 'Full source on GitHub. Fork, audit, extend. No lock-in, no surprise pricing, no ToS traps.' },
];

const COMPARE = [
  { feature: 'Self-hosted', tunnelr: 'Yes', ngrok: 'No', cf: 'No' },
  { feature: 'Unlimited tunnels', tunnelr: 'Yes', ngrok: 'Paid', cf: 'Yes' },
  { feature: 'Persistent URLs', tunnelr: 'Yes', ngrok: 'No', cf: 'Yes' },
  { feature: 'Custom domains', tunnelr: 'Yes', ngrok: 'Paid', cf: 'Yes' },
  { feature: 'Automatic SSL', tunnelr: 'Yes', ngrok: 'Yes', cf: 'Yes' },
  { feature: 'No account needed', tunnelr: 'Yes', ngrok: 'No', cf: 'No' },
  { feature: 'Open source', tunnelr: 'Yes', ngrok: 'No', cf: 'No' },
  { feature: 'Zero telemetry', tunnelr: 'Yes', ngrok: 'No', cf: 'No' },
  { feature: 'Data stays on your infra', tunnelr: 'Yes', ngrok: 'No', cf: 'No' },
  { feature: 'Free forever', tunnelr: 'Yes', ngrok: 'No', cf: 'Yes' },
];

const FAQS = [
  { q: 'What is Tunnelr?', a: 'A free, open-source, self-hosted localhost tunnel built in Go. Expose local dev servers through your own domain — like ngrok, but you own and control the infrastructure. Uses Caddy for automatic SSL and WebSockets for persistent tunnel connections.' },
  { q: 'How is it different from ngrok?', a: "ngrok routes traffic through their servers, limits free users to one tunnel with 2-hour session expiry, and charges for custom domains and persistent URLs. Tunnelr runs on your infrastructure with unlimited tunnels, persistent URLs, custom domains and zero time limits — all free." },
  { q: 'How do I deploy the server?', a: "Docker Compose on any VPS. Clone the repo, copy .env.example to .env, set your domain and SSL email, then `docker compose up -d`. That gives you Caddy (reverse proxy + SSL), the Go tunnel server, and everything wired together. ~5 minutes." },
  { q: 'How do I connect from my local machine?', a: "Build the CLI with `go build -o tunnelr ./cmd/cli` then run `TUNNELR_SERVER=wss://yourdomain.com/ws tunnelr connect 3000` to expose local port 3000. You'll get a public URL like yourdomain.com/t/a1b2c3 immediately." },
  { q: 'What are the routing modes?', a: 'Path mode (yourdomain.com/t/tunnel-id/endpoint) — single DNS A record, HTTP-01 SSL. Subdomain mode (tunnel-id.yourdomain.com) — wildcard DNS, DNS-01 via Cloudflare.' },
  { q: 'Can I use it for production webhooks?', a: 'Yes. Persistent WebSocket connections with no session timeouts make it reliable for webhook development, API testing and staging environments that need stable public URLs.' },
  { q: 'System requirements?', a: 'Server: any Linux VPS with Docker and a public IP (512MB RAM is enough). Client: Go 1.21+ to build the CLI binary, or pre-built binaries for Linux/macOS/Windows.' },
  { q: 'Is it really free?', a: 'Yes — MIT-licensed. You only pay for the VPS you host the server on. A $5/month droplet handles most team workloads.' },
];

export function TunnelrA({ onNav }) {
  return (
    <EditorialArticle
      navPage="opensource"
      onNav={onNav}
      eyebrow="Open Source · Labs"
      title={<>The ngrok alternative you <em>actually&nbsp;own</em>.</>}
      lead="Tunnelr is a self-hosted localhost tunnel built in Go. Unlimited tunnels, persistent URLs, automatic Caddy SSL, two routing modes. Deploy on your VPS in five minutes."
      primaryCta={
        <a
          className="btnA-primary"
          href={GH}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackOutboundClick(GH, 'tunnelr_hero_github')}
        >
          View on GitHub
        </a>
      }
      secondaryCta={<a className="btnA-ghost" href="#compare">Compare to ngrok ↓</a>}
    >
      <SEO
        title="Tunnelr | Open Source ngrok Alternative | Self-Hosted"
        description="Tunnelr is a free, open-source, self-hosted localhost tunnel built in Go. Unlimited domains, persistent URLs, automatic Caddy SSL. The ngrok alternative you own."
        keywords={['tunnelr', 'ngrok alternative', 'open source ngrok', 'self-hosted tunnel', 'localhost tunnel']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Open Source', url: '/opensource' },
          { name: 'Tunnelr' },
        ]}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Tunnelr',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Linux, macOS, Windows',
            description:
              'A free, open-source, self-hosted localhost tunnel built in Go. Unlimited tunnels, persistent URLs, automatic SSL.',
            url: 'https://www.aakeeo.com/tunnelr',
            downloadUrl: GH,
            license: 'https://opensource.org/licenses/MIT',
            programmingLanguage: 'Go',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            author: { '@type': 'Organization', name: 'Aakeeo Technologies', url: 'https://www.aakeeo.com' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />

      <section>
        <h2 className="serviceA-h2">What it ships with.</h2>
        <div className="pillA-grid pillA-grid--3">
          {FEATURES.map((f) => (
            <div key={f.title} className="pillA-card">
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="compare">
        <h2 className="serviceA-h2">Tunnelr vs ngrok vs Cloudflare Tunnel.</h2>
        <div className="articleA-prose" style={{ maxWidth: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Tunnelr</th>
                <th>ngrok</th>
                <th>Cloudflare Tunnel</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((r) => (
                <tr key={r.feature}>
                  <td>{r.feature}</td>
                  <td>{r.tunnelr}</td>
                  <td>{r.ngrok}</td>
                  <td>{r.cf}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="serviceA-h2">FAQ</h2>
        <div className="solA-faq-list">
          {FAQS.map((f, i) => (
            <details key={f.q} className="solA-faq-item">
              <summary>
                <span className="solA-faq-num">{String(i + 1).padStart(2, '0')}</span>
                <span>{f.q}</span>
                <span className="solA-faq-chev">＋</span>
              </summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section>
        <EmailCapture
          source="tunnelr_page"
          resourceName="Tunnelr release notes"
          resourceType="updates"
          eyebrow="Free · Release notes"
          title={<>Stay in the loop on <em>Tunnelr</em>.</>}
          description="Drop your email and we'll reach out personally when there's a meaningful release or a deployment pattern worth sharing. No drip sequence, no list."
          cta="Keep me posted"
        />
      </section>

      <section className="ctaA" style={{ padding: 0, marginTop: 48 }}>
        <div className="ctaA-inner">
          <h2>Ship it on your infra.</h2>
          <p>MIT-licensed. Self-hostable on any VPS. Pull the repo and be serving tunnels in five minutes.</p>
          <div className="ctaA-buttons">
            <a
              className="btnA-primary"
              href={GH}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick(GH, 'tunnelr_cta_github')}
            >
              Get Tunnelr on GitHub
            </a>
          </div>
        </div>
      </section>
    </EditorialArticle>
  );
}
